import React, { useState, useRef } from 'react';
import TooltipMUI from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { ClickAwayListener } from '@mui/material';
import './Tooltip.scss';

const Tooltip = (props) => {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef(null);
	const { title, content } = props;

	const handleMouseEnter = () => {
		setOpen(true);
	};

	const handleMouseLeave = () => {
		setOpen(false);
	};

	const handleClickAway = (event) => {
		if (wrapperRef?.current && !wrapperRef?.current?.contains(event?.target)) {
			setOpen(false);
		}
	};

	const handleClick = () => {
		const { openOnClick, closeOnClick } = props;

		// If both are enabled, treat click as toggle.
		if (openOnClick && closeOnClick) {
			setOpen((prev) => !prev);
			return;
		}

		if (openOnClick) {
			setOpen(true);
			return;
		}

		if (closeOnClick) {
			setOpen(false);
		}
	};

	const directionMap = {
		down: 'bottom',
		up: 'top',
		left: 'left',
		right: 'right',
	};

	const placement = directionMap[props?.direction] || 'bottom';

	const tooltipContent = title ? (
		<div>
			<div className="feature-compatibility">{title}</div>
			<div className="feature-compatibility-body">{content}</div>
		</div>
	) : (
		content
	);

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<div
				ref={wrapperRef}
				style={{
					...(props.inline && { display: 'inline-block' }),
				}}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={handleClick} // Hide tooltip when element clicked
			>
				<TooltipMUI
					open={open}
					disableFocusListener
					disableHoverListener
					disableTouchListener
					// followCursor
					enterDelay={1000}
					// leaveDelay={200}
					title={tooltipContent}
					placement={placement}
					PopperProps={{
						sx: {
							pointerEvents: 'none',
						},
						modifiers: [
							{
								name: 'preventOverflow',
								options: {
									boundary: 'clippingParents', // or a custom element
									padding: 8,
								},
							},
							{
								name: 'offset',
								options: {
									offset: [
										typeof props.offsetX === 'number'
											? props.offsetX
											: 0,
										typeof props.offset === 'number'
											? props.offset
											: -10,
									],
								},
							},
						],
					}}
					componentsProps={{
						tooltip: {
							sx: {
								padding: title ? '12px 16px' : '4px 8px',
								borderRadius: '4px',
								boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.16)',
								border: 'solid 1px #dedede',
								backgroundColor: '#fff',
								color: '#000',
								fontFamily: 'Inter-Regular',
								fontSize: '12px',
								fontWeight: 'normal',
								maxWidth: props?.style?.width ?? '280px',
							},
						},
					}}
				>
					<div style={{ all: 'inherit', ...(props.inline ? { display: 'inline-block' } : {}), }}>{props?.children}</div>
				</TooltipMUI>
			</div>
		</ClickAwayListener>
	);
};

Tooltip.defaultProps = {
	direction: 'bottom',
	offset: -10,
	offsetX: 0,
	width: '280px',
	closeOnClick: true,
	openOnClick: false,
	inline: false,
	title: '',
};

Tooltip.propTypes = {
	children: PropTypes.node.isRequired,
	content: PropTypes.node.isRequired,
	title: PropTypes.node,
	style: PropTypes.object,
	direction: PropTypes.string,
	offset: PropTypes.number,
	width: PropTypes.string,
	offsetX: PropTypes.number,
	inline: PropTypes.bool,
	closeOnClick: PropTypes.bool,
	openOnClick: PropTypes.bool,
};

export default Tooltip;
